import { useCallback, useEffect, useReducer } from 'react';

import {
  ApiManager,
  BonusApi,
  Bus,
  NotificationManager,
  SiteConfigManager,
  StorageManager,
  useApplicationState,
  useSettings,
} from '@atlas/core';

import {
  BONUS_CONFLICT_ERROR_CODES,
  BONUS_CONFLICT_ERROR_MESSAGES,
  bonusStatuses,
  POPUP_SIGN_UP_DELAY_STORAGE_KEYS,
} from 'common/constants';
import { getBonusActivationToastOptions } from 'src/common/utils/depositBonusUtils';

const SET_STATE = 'SET_STATE';

const reducer = (state, { payload, type }) => {
  switch (type) {
    case SET_STATE: {
      return {
        ...state,
        ...payload,
      };
    }
    default: {
      return state;
    }
  }
};

const initialState = {
  activeBonuses: [],
  bonuses: [],
  isLoading: true,
};

const {
  ACTIVATED,
  ACTIVATION_EXPIRED,
  AVAILABLE,
  CANCELED,
  COMPLETED,
  DISABLED,
  EXPIRED,
  IN_PROGRESS,
  PENDING,
  SPEND,
} = bonusStatuses;

const statusOrder = {
  [ACTIVATED]: 3,
  [ACTIVATION_EXPIRED]: 100,
  [AVAILABLE]: 1,
  [CANCELED]: 8,
  [COMPLETED]: 5,
  [DISABLED]: 9,
  [EXPIRED]: 7,
  [IN_PROGRESS]: 2,
  [PENDING]: 4,
  [SPEND]: 6,
};

const getBlockedBonus = ({ activeBonuses, blockedBonusInstanceId, bonus, error }) => {
  if (blockedBonusInstanceId) {
    return activeBonuses?.find(el => String(el?.id) === String(blockedBonusInstanceId));
  }

  const activeProductType =
    bonus?.productType ||
    activeBonuses?.find(el => String(el?.bonus?.id) === String(error?.bonusId))?.bonus
      ?.productType;

  return activeBonuses?.find(el => {
    const isActiveBonus = el?.status === IN_PROGRESS && el?.bonus?.bonusStatus === 'ACTIVE';

    return isActiveBonus && el?.bonus?.productType === activeProductType;
  });
};

const getStringValues = values => values.filter(value => typeof value === 'string');

const getErrorValues = error =>
  getStringValues([
    error?.code,
    error?.message,
    error?.error,
  ]);

const getNestedErrorValues = error =>
  getStringValues([
    error?.code,
    error?.message,
  ]);

const getBonusConflictErrorValues = error => {
  if (typeof error === 'string') {
    return [error];
  }

  return [
    ...getErrorValues(error),
    ...getNestedErrorValues(error?.error),
    ...getErrorValues(error?.data),
    ...getErrorValues(error?.response),
  ];
};

const getBlockedBonusInstanceId = error =>
  error?.blockedBonusInstanceId ??
  error?.data?.blockedBonusInstanceId ??
  error?.response?.blockedBonusInstanceId ??
  error?.error?.blockedBonusInstanceId;

const isBonusConflictError = error => {
  const errorValues = getBonusConflictErrorValues(error);
  const conflictErrorValues = [...BONUS_CONFLICT_ERROR_CODES, ...BONUS_CONFLICT_ERROR_MESSAGES];

  return (
    getBlockedBonusInstanceId(error) != null ||
    errorValues.some(value =>
      conflictErrorValues.some(conflictErrorValue => value.includes(conflictErrorValue)),
    )
  );
};

export const useShowPopupBonuses = () => {
  const authorizationToken = ApiManager.getAuthorizationToken();

  useEffect(() => {
    if (StorageManager.getItem(POPUP_SIGN_UP_DELAY_STORAGE_KEYS.BONUSES)) {
      StorageManager.removeItem(POPUP_SIGN_UP_DELAY_STORAGE_KEYS.BONUSES);
      const isShowBonusPopup = SiteConfigManager.getConfig(
        'config.config.registration.showBonusPopup',
      );

      if (authorizationToken && isShowBonusPopup) {
        setTimeout(() => {
          NotificationManager.display({
            data: {},
            type: 'registrationBonus',
          });
        });
      }
    }
  }, [authorizationToken]);
};

const useBonuses = () => {
  const [{ activeBonuses, bonuses, isLoading }, dispatch] = useReducer(reducer, initialState);

  const applicationState = useApplicationState();
  const { account: { currency } = {}, wallet } = applicationState;
  const { lang } = useSettings();

  const fetchBonuses = useCallback(async () => {
    dispatch({ payload: { isLoading: true }, type: SET_STATE });

    const country = applicationState.account.country || null;

    try {
      const [{ bonuses: newBonuses }, { bonuses: newActiveBonuses }] = await Promise.all([
        BonusApi.getAvailableBonuses({
          country,
          currency,
          language: lang.toUpperCase(),
        }),
        BonusApi.getActiveBonuses(lang.toUpperCase()),
      ]);

      dispatch({
        payload: {
          activeBonuses: newActiveBonuses
            ?.filter(({ status }) => status !== ACTIVATION_EXPIRED)
            ?.sort(
              ({ status: statusA }, { status: statusB }) =>
                statusOrder[statusA] - statusOrder[statusB],
            ),
          bonuses: newBonuses,
          isLoading: false,
        },
        type: SET_STATE,
      });
    } catch (error) {
      dispatch({ payload: { isLoading: false }, type: SET_STATE });
    }
  }, [applicationState.account.country, currency, lang]);

  useEffect(() => {
    if (currency) {
      fetchBonuses();
    }
  }, [currency, fetchBonuses]);

  useEffect(() => {
    Bus.on(Bus.events.profile.depositComplete, fetchBonuses);

    return () => Bus.off(Bus.events.profile.depositComplete, fetchBonuses);
  }, [fetchBonuses]);

  const activateBonus = useCallback(
    async bonus => {
      const { id, wagerFreeSpinInstanceId } = bonus;

      try {
        await BonusApi.activateBonus(id, wagerFreeSpinInstanceId);
        NotificationManager.toast(getBonusActivationToastOptions(bonus, lang.toUpperCase()));
        await fetchBonuses();
      } catch (err) {
        const isBonusConflict = isBonusConflictError(err);

        if (isBonusConflict) {
          const blockedBonusInstanceId = getBlockedBonusInstanceId(err);
          const blockedBonus = getBlockedBonus({
            activeBonuses,
            blockedBonusInstanceId,
            bonus,
            error: err,
          });
          const conflictingBonusId = blockedBonusInstanceId ?? blockedBonus?.id;
          const productType = blockedBonus?.bonus?.productType ?? bonus?.productType;

          NotificationManager.display({
            data: { conflictingBonusId, productType },
            type: 'bonusConflict',
          });
        } else {
          NotificationManager.notify({
            data: err,
            message: err.code || '',
            status: 'error',
            type: 'notification',
          });
        }
      }
    },
    [activeBonuses, fetchBonuses],
  );

  const deactivateBonus = useCallback(
    async bonus => {
      const { id } = bonus;
      await BonusApi.cancelBonus(id);
      await fetchBonuses();
    },
    [fetchBonuses],
  );

  return {
    activateBonus,
    activeBonuses,
    bonuses,
    deactivateBonus,
    fetchBonuses,
    isLoading,
    wallet,
  };
};

export default useBonuses;
